import { useQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { CircleDollarSign, Clock } from 'lucide-react';
import { useId, useMemo, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo } from '@/componentes/Campo';
import { CampoDeCompetencia } from '@/componentes/CampoDeData';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra, Carregando, EsqueletoTexto } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Medida } from '@/componentes/Medida';
import { Seletor } from '@/componentes/Seletor';
import { SemPermissao } from '@/componentes/SemPermissao';
import { RodapeTabela, Tabela, type Coluna } from '@/componentes/Tabela';
import { rotuloDaMargem, rotuloDoResultado } from '@/modulos/custos/margem';
import { usePaginacao } from '@/modulos/operacao/paginacao';
import { formatarData, formatarDecimal, formatarInteiro, formatarReais, formatarReaisCompacto } from '@/lib/formato';
import { rotuloDoCriterio } from '@/modulos/custos/api';
import { explicarFalha } from '@/lib/pedido';
import { perfilAPedir } from '@/lib/perfil-da-licenca';
import { useSessao } from '@/modulos/sessao/consultas';
import { variacaoDoPeriodo } from '@/lib/variacao';

import {
  PREMISSA_DO_SINAL,
  apiDoGestor,
  contextoDoCustoPorHoraProdutiva,
  dataDoDia,
  definicaoDoExecutivo,
  diaDaData,
  ehSoReceita,
  gruposDaFila,
  margemEscrita,
  modulosForaDaConta,
  baseDaDivisaoEscrita,
  motivoNaoAtribuidoEscrito,
  oQueResolveONaoAtribuido,
  pedeOResultado,
  porqueOResultadoEParcial,
  recorteEscrito,
  type EixoDaFila,
  type ItemDaFila,
  type ResultadoDoContrato,
  type ResultadoSoDaReceita,
  type Sinal,
} from './gestor';
import { FilaAgrupada } from './FilaAgrupada';
import { OPCOES_POR_PAGINA, PADRAO_POR_PAGINA, contagem, fatiarPagina } from './painel';
import { montarBuscaDePeriodo, periodoDaBusca } from '@/lib/busca-de-periodo';

/**
 * Módulo 21 — a visão do gestor, na fatia do ciclo 1: o painel executivo do
 * período e a FILA DE PRIORIDADES por máquina, com o porquê de cada lugar.
 *
 * A regra da fila está escrita na própria tela, com os pesos que vieram do
 * servidor. O v6 escreve o risco da ferramenta: "decidir por urgência
 * aparente". Um número de prioridade sem explicação é só outra urgência
 * aparente.
 *
 * Custo, receita e resultado (ferramenta 3) entrou em 01/10, quando a receita
 * do módulo 14 passou a existir. Ela é a `SecaoDoResultado`, no fim desta tela,
 * e tem período próprio: a receita é mensal, e o período da faixa de cima não
 * se aplica a ela. Nada lá se chama lucro, e as premissas aparecem ANTES da
 * tabela — o risco que o v6 escreve para o módulo 11 é "chamar custo parcial
 * de margem final", e o painel executivo é onde isso custa mais.
 */

/* O cálculo do período padrão saiu daqui para `src/lib/busca-de-periodo.ts`,
   compartilhado com Custos: dois cálculos iguais divergiriam na primeira
   correção, e o que ficasse para trás aceitaria a data que o outro recusa. Lá
   ele também é inteiro em UTC — misturar um `de` do fuso local com um `ate`
   cortado em UTC dá período de 31 dias em parte do ano, e o número sai
   plausível. */

/** A ordem em que a regra é lida: da parcela mais grave para a mais leve. */
const ORDEM_DA_REGRA: readonly Sinal[] = [
  'preventivaVencida',
  'ordemComMaquinaParada',
  'divergenciaAberta',
  'custoForaDaCurva',
  'ordemAberta',
  'consumoForaDaFaixa',
  'turnoParadoEvitavel',
];

const textoDoMotivo = (m: ItemDaFila['motivos'][number]) => `${m.frase} (${contagem(m.pontos, 'ponto', 'pontos')})`;

/* O recorte da faixa (R06, p.72): Dia ou Período. Com Dia, as duas datas
   viram um campo só. */
const RECORTES: readonly { readonly valor: 'dia' | 'periodo'; readonly rotulo: string }[] = [
  { valor: 'dia', rotulo: 'Dia' },
  { valor: 'periodo', rotulo: 'Período' },
];

/* A fila por máquina, ou agrupada por obra ou por centro de custo (0091; R06, p.72). */
const EIXOS: readonly { readonly valor: EixoDaFila; readonly rotulo: string }[] = [
  { valor: 'maquina', rotulo: 'Máquina' },
  { valor: 'obra', rotulo: 'Obra' },
  { valor: 'centro', rotulo: 'Centro de custo' },
];

/* Abaixo de 768 a fila vira lista de cartões (R01, p.28), e no toque não há
   `title` para mostrar o que a reticência escondeu: ali o texto quebra linha
   em vez de ser cortado. A consulta é a mesma da Tabela (`width < 768px`). */
const QUEBRA_NO_CARTAO = 'max-[768px]:max-w-none max-[768px]:whitespace-normal max-[768px]:text-pretty';

/* Teto da coluna "Por quê" na tabela: o que sobra do quadro depois das outras
   colunas, medido pela largura do próprio quadro (`@container`, unidade
   `cqw`). Com as cinco colunas (1280 para cima) sobram 100cqw - 680 px; sem
   as duas secundárias, 100cqw - 420 px; com o quadro de 1280 px ou mais, a
   descrição da máquina ganha 40ch e sobram 100cqw - 744 px. Assim a fila
   cabe inteira sem rolar de lado em L e em M, e o motivo só ganha reticência
   — com o texto inteiro no `title` — quando de fato não cabe. */
const TETO_DO_PORQUE =
  'max-w-[max(24ch,calc(100cqw_-_680px))] @min-[1280px]:max-w-[max(24ch,calc(100cqw_-_744px))] max-[1280px]:max-w-[max(24ch,calc(100cqw_-_420px))] max-[768px]:max-w-none';

/* A descrição da máquina, segunda linha da identidade: 32ch, e 40ch quando o
   quadro passa de 1280 px (janela de 1600 para cima), onde sobra lugar. */
/* O mesmo padrão de `PaginaEquipamentos`: a rota se alcança por
   `getRouteApi`, sem precisar exportá-la de `src/rotas`. */
const rota = getRouteApi('/painel/gestor');

const TETO_DA_DESCRICAO = 'max-w-[32ch] @min-[1280px]:max-w-[40ch]';

/**
 * A célula "Por quê" — da máquina e do grupo (0091), com o mesmo desenho.
 * Altura fixa de 48 px (p.18): o sinal que mais pesa na primeira linha; os
 * outros na segunda. Na tabela, o que passa do teto da coluna ganha
 * reticência, com o texto inteiro no `title` e para o leitor de tela; no
 * cartão, quebra linha. A regra completa está logo abaixo da fila.
 */
function PorQue({ motivos }: { readonly motivos: ItemDaFila['motivos'] }) {
  const [principal, ...outros] = [...motivos].sort((a, b) => b.pontos - a.pontos);
  const resto = outros.map(textoDoMotivo).join(' · ');
  return (
    <span className={`flex flex-col ${TETO_DO_PORQUE}`}>
      {principal ? (
        <span className={`truncate text-tinta ${QUEBRA_NO_CARTAO}`} title={textoDoMotivo(principal)}>
          {textoDoMotivo(principal)}
        </span>
      ) : (
        <span className="text-tinta">—</span>
      )}
      {outros.length > 0 ? (
        <span className={`txt-apoio truncate text-tinta-secundaria ${QUEBRA_NO_CARTAO}`} title={resto}>
          {`+ ${contagem(outros.length, 'sinal', 'sinais')}: ${resto}`}
        </span>
      ) : null}
    </span>
  );
}

export function PaginaGestor() {
  const base = useId();
  const navegar = useNavigate();

  /* O PERÍODO MORA NA URL (p.16). Em `useState`, recarregar voltava sempre aos
     últimos 30 dias, e mandar o link mandava uma tela diferente da que se
     estava vendo — o que é pior do que não mandar, porque quem recebe acha que
     está vendo a mesma coisa. Num painel de gestão a conversa é "olha esse mês
     aqui", e o link tem de levar o mês. */
  const busca = rota.useSearch();
  const periodo = useMemo(() => periodoDaBusca(busca), [busca]);
  const { de, ate } = periodo;
  /* O DIA e o AGRUPAMENTO DA FILA também moram na URL (0091), pelo mesmo
     motivo do período: "a fila por obra de ontem" é um link que se manda. */
  const dia = busca.dia ?? null;
  const eixo: EixoDaFila = busca.fila ?? 'maquina';
  const comFila = busca.fila ? { fila: busca.fila } : {};
  const comPeriodo = busca.de !== undefined && busca.ate !== undefined ? { de: busca.de, ate: busca.ate } : {};

  /* Trocar o período é NAVEGAR, não mexer em estado: assim o botão de voltar do
     navegador desfaz a troca, que é o que a pessoa espera dele. Escolher datas
     sai do recorte diário. */
  const irPara = (novo: { readonly de: string; readonly ate: string }) => {
    void navegar({ to: '/painel/gestor', search: { ...montarBuscaDePeriodo(novo), ...comFila } });
  };
  const escolherRecorte = (recorte: 'dia' | 'periodo') => {
    void navegar({
      to: '/painel/gestor',
      search: { ...comPeriodo, ...(recorte === 'periodo' ? {} : { dia: 'hoje' as const }), ...comFila },
    });
  };
  /* O campo único do Dia (p.72): hoje e ontem vão ao endereço pelo nome. */
  const hojeNaOperacao = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
  const escolherDia = (data: string) => {
    const novo = diaDaData(data, hojeNaOperacao);
    if (novo === null) return;
    void navegar({ to: '/painel/gestor', search: { ...comPeriodo, dia: novo, ...comFila } });
  };
  const escolherEixo = (novo: EixoDaFila) => {
    setPagina(1);
    void navegar({
      to: '/painel/gestor',
      search: { ...comPeriodo, ...(dia ? { dia } : {}), ...(novo === 'maquina' ? {} : { fila: novo }) },
    });
  };
  const setDe = (valor: string) => irPara({ de: valor, ate });
  const setAte = (valor: string) => irPara({ de, ate: valor });

  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(PADRAO_POR_PAGINA);

  const visao = useQuery({
    queryKey: dia ? ['painel', 'gestor', 'dia', dia] : ['painel', 'gestor', de, ate],
    queryFn: () => (dia ? apiDoGestor.visaoDoDia(dia) : apiDoGestor.visao(de, ate)),
  });
  const v = visao.data;
  const fora = v ? modulosForaDaConta(v.sinais) : [];

  const COLUNAS: readonly Coluna<ItemDaFila & { posicao: number }>[] = [
    {
      /* A primeira coluna é a identidade da linha e fica fixa ao rolar de
         lado (p.18). A posição vem logo depois: sem o código, "1º" não diz
         de qual máquina é a linha. */
      chave: 'maquina',
      rotulo: 'Máquina',
      celula: (p) => (
        <span className="flex flex-col">
          <span className="codigo text-tinta">{p.codigo}</span>
          <span
            className={`txt-apoio block truncate text-tinta-secundaria ${TETO_DA_DESCRICAO} ${QUEBRA_NO_CARTAO}`}
            title={p.descricao}
          >
            {p.descricao}
          </span>
        </span>
      ),
    },
    {
      chave: 'posicao',
      rotulo: 'Posição',
      alinhamento: 'numero',
      celula: (p) => <span className="numero text-tinta">{`${formatarInteiro(p.posicao)}º`}</span>,
    },
    {
      chave: 'porque',
      rotulo: 'Por quê',
      celula: (p) => <PorQue motivos={p.motivos} />,
    },
    {
      /* Secundárias (R01, p.28): somem abaixo de 1280 e no cartão. A ordem da
         fila já diz quem soma mais, e os pontos de cada sinal vão no motivo. */
      chave: 'pontos',
      rotulo: 'Pontos',
      historia: 'COL-PNL-07',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (p) => <span className="numero text-tinta">{formatarInteiro(p.pontos)}</span>,
    },
    {
      chave: 'custoHora',
      rotulo: 'Custo/h produtiva',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (p) =>
        p.custoPorHora === null ? (
          <span className="txt-apoio whitespace-nowrap text-tinta-secundaria">sem hora</span>
        ) : (
          <Medida valor={formatarReais(p.custoPorHora)} unidade="/h" colado />
        ),
    },
  ];

  const linhas = (v?.fila ?? []).map((p, i) => ({ ...p, posicao: i + 1 }));
  const fatia = fatiarPagina(linhas, pagina, porPagina);
  const grupos = v ? gruposDaFila(v, eixo) : eixo === 'maquina' ? null : [];

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Painel"
          titulo="Visão do gestor"
          subtitulo="O que o período custou, e onde agir primeiro — com o porquê escrito"
        />

        <section className="cartao flex flex-wrap items-end gap-4 p-6">
          {/* O RECORTE DIÁRIO (0091 · "painel executivo diário"): o dia é
              resolvido no servidor, no fuso da operação, e a faixa compara
              com o dia anterior. No dia, as datas saem de cena. */}
          {/* RECORTE NO CARTÃO DO FILTRO, À ESQUERDA (R06, p.72): Dia ou
              Período. Com Dia, as duas datas viram um campo só; o recorte vai
              para o endereço, com os demais filtros. */}
          <div className="flex items-center gap-3 self-end">
            <span aria-hidden="true" className="txt-botao text-tinta">
              Recorte
            </span>
            <Seletor<'dia' | 'periodo'> rotulo="Recorte" valor={dia ? 'dia' : 'periodo'} opcoes={RECORTES} aoEscolher={escolherRecorte} />
          </div>
          {dia ? (
            <Campo id={`${base}-dia`} rotulo="Dia">
              <input
                id={`${base}-dia`}
                type="date"
                className="controle"
                max={hojeNaOperacao}
                value={dataDoDia(dia, hojeNaOperacao)}
                onChange={(e) => {
                  escolherDia(e.target.value);
                }}
              />
            </Campo>
          ) : (
            <>
              <Campo id={`${base}-de`} rotulo="De">
                <input
                  id={`${base}-de`}
                  type="date"
                  className="controle"
                  value={de}
                  onChange={(e) => {
                    setDe(e.target.value);
                  }}
                />
              </Campo>
              <Campo id={`${base}-ate`} rotulo="Até">
                <input
                  id={`${base}-ate`}
                  type="date"
                  className="controle"
                  value={ate}
                  onChange={(e) => {
                    setAte(e.target.value);
                  }}
                />
              </Campo>
            </>
          )}
          {v ? (
            <p className="txt-apoio text-tinta-secundaria">
              {`${recorteEscrito(v)} ${contagem(v.maquinasAtivas, 'máquina', 'máquinas')} na frota.`}
            </p>
          ) : visao.isPending ? (
            <Barra className="mb-3 h-3 w-32" />
          ) : null}
        </section>

        {visao.isError ? (
          <EstadoVazio
            titulo="Não foi possível montar a visão do gestor"
            descricao={explicarFalha(visao.error).comoResolver}
            acao={
              <Botao
                nivel="secundario"
                onClick={() => {
                  void visao.refetch();
                }}
              >
                Carregar de novo
              </Botao>
            }
          />
        ) : (
          <>
            {/* Contexto curto, de base ou período (p.17): a faixa se lê de
                relance. A definição de cada indicador vai no texto logo
                abaixo dela. */}
            <FaixaIndicadores>
              <CardIndicador
                rotulo="Custo direto"
                icone={CircleDollarSign}
                tom="marca"
                carregando={visao.isPending}
                valor={v ? formatarReais(v.executivo.custoTotal) : ''}
                contexto={
                  !v
                    ? ''
                    : v.executivo.semPreco > 0
                      ? `parcial · ${contagem(v.executivo.semPreco, 'lançamento sem preço', 'lançamentos sem preço')}`
                      : 'direto, sem depreciação nem indireto'
                }
                /* A VARIACAO CONTRA O PERIODO ANTERIOR (R02, p.31), e custo
                   que sobe PIOROU. Ela nao aparece sem base de comparacao:
                   `variacaoDoPeriodo` devolve nulo, e o cartao fica so com o
                   valor — "+100%" sobre base zero parece medida e e so a base
                   faltando. */
                {...(v ? (variacaoDoPeriodo(v.executivo.custoTotal, v.anterior.custoTotal, 'subir_e_pior') ?? {}) : {})}
                aoAbrir={() => {
                  void navegar({ to: '/custos' });
                }}
              />
              <CardIndicador
                historia="IND-PNL-05"
                rotulo="Custo por hora produtiva"
                icone={CircleDollarSign}
                tom="marca"
                carregando={visao.isPending}
                valor={v?.executivo.custoPorHoraProdutiva == null ? '—' : formatarReais(v.executivo.custoPorHoraProdutiva)}
                {...(v?.executivo.custoPorHoraProdutiva == null ? {} : { unidade: '/h', unidadeColada: true })}
                contexto={!v ? '' : contextoDoCustoPorHoraProdutiva(v.executivo)}
                {...(v
                  ? (variacaoDoPeriodo(
                      v.executivo.custoPorHoraProdutiva,
                      v.anterior.custoPorHoraProdutiva,
                      'subir_e_pior',
                    ) ?? {})
                  : {})}
                aoAbrir={() => {
                  void navegar({ to: '/custos' });
                }}
              />
              <CardIndicador
                rotulo="Paradas evitáveis"
                icone={Clock}
                tom="parada"
                carregando={visao.isPending}
                valor={v?.executivo.horasParadasEvitaveis == null ? '—' : formatarDecimal(v.executivo.horasParadasEvitaveis, 1)}
                {...(v?.executivo.horasParadasEvitaveis == null ? {} : { unidade: 'h' })}
                contexto={
                  !v ? '' : !v.sinais.operacao ? 'seu perfil não lê a operação' : dia ? 'encerradas no dia' : 'encerradas no período'
                }
                {...(v
                  ? (variacaoDoPeriodo(
                      v.executivo.horasParadasEvitaveis,
                      v.anterior.horasParadasEvitaveis,
                      'subir_e_pior',
                    ) ?? {})
                  : {})}
                aoAbrir={() => {
                  void navegar({ to: '/operacao' });
                }}
              />
              <CardIndicador
                historia="IND-PNL-06"
                rotulo="Horas a explicar"
                icone={Clock}
                tom="standby"
                carregando={visao.isPending}
                valor={v?.executivo.horasAExplicar == null ? '—' : formatarDecimal(v.executivo.horasAExplicar, 1)}
                {...(v?.executivo.horasAExplicar == null ? {} : { unidade: 'h' })}
                contexto={
                  !v
                    ? ''
                    : v.executivo.divergenciasAbertas === null
                      ? 'seu perfil não lê a operação'
                      : contagem(
                          v.executivo.divergenciasAbertas,
                          'divergência aberta com o cliente',
                          'divergências abertas com o cliente',
                        )
                }
                aoAbrir={() => {
                  void navegar({ to: '/operacao/conciliacao' });
                }}
              />
            </FaixaIndicadores>

            {v ? (
              <p className="txt-apoio max-w-[80ch] text-pretty text-tinta-secundaria">
                {definicaoDoExecutivo(v.executivo)}
              </p>
            ) : (
              <div aria-hidden="true" className="flex max-w-[80ch] flex-col gap-2 py-1">
                <Barra className="h-3 w-full" />
                <Barra className="h-3 w-3/5" />
              </div>
            )}

            <section className="flex flex-col gap-2">
              <h2 className="txt-secao text-tinta">Onde agir primeiro?</h2>
              {/* "Fila por" abaixo do título, à esquerda (R06, p.72). */}
              <div className="flex items-center gap-3">
                <span aria-hidden="true" className="txt-apoio text-tinta-secundaria">
                  Fila por
                </span>
                <Seletor<EixoDaFila> rotulo="Fila por" valor={eixo} opcoes={EIXOS} aoEscolher={escolherEixo} />
              </div>
              <p className="txt-apoio max-w-[80ch] text-pretty text-tinta-secundaria">
                A fila soma pontos por sinal, com a regra escrita logo abaixo. Só entra máquina com algum sinal; no
                empate, vem antes a que tem mais preventiva vencida.
                {fora.length > 0 ? ` Seu perfil não lê ${fora.join(', ')}: esses sinais ficaram fora da conta.` : ''}
                {eixo === 'maquina'
                  ? ''
                  : ` Por ${eixo === 'obra' ? 'obra' : 'centro de custo'}, cada linha soma os pontos e o custo parado das máquinas alocadas nela agora${eixo === 'centro' ? ' — o centro da alocação ou, sem ele, o da obra' : ''}, da maior para a menor.`}
              </p>
            </section>

            {/* O teto da coluna "Por quê" mede a largura da fila (`cqw`). O
                QuadroTabela também é `@container`; este garante a medida da
                fila, e não a da janela, mesmo que ele deixe de ser. */}
            <div className="@container">
              {grupos !== null && eixo !== 'maquina' ? (
                <FilaAgrupada
                  eixo={eixo}
                  grupos={grupos}
                  carregando={visao.isPending}
                  vazio={
                    <EstadoVazio
                      titulo={eixo === 'obra' ? 'Nenhuma obra com sinal de prioridade' : 'Nenhum centro de custo com sinal de prioridade'}
                      descricao="Nenhuma máquina tem preventiva vencida, O.S. aberta, divergência de horas, consumo fora da faixa, parada evitável ou custo fora da curva no período."
                      acao={
                        <Link to="/painel" className="botao botao-secundario">
                          Abrir painel
                        </Link>
                      }
                    />
                  }
                />
              ) : (
              <Tabela
                rotulo="Fila de prioridades"
                colunas={COLUNAS}
                linhas={fatia.visiveis}
                alturaDupla
                chaveDaLinha={(p) => p.equipamentoId}
                carregando={visao.isPending}
                vazio={
                  <EstadoVazio
                    titulo="Nenhuma máquina com sinal de prioridade"
                    descricao="Sem preventiva vencida, O.S. aberta, divergência de horas, consumo fora da faixa, parada evitável ou custo fora da curva no período."
                    acao={
                      <Link to="/painel" className="botao botao-secundario">
                        Abrir painel
                      </Link>
                    }
                  />
                }
                /* Toda lista filtrada diz quantas linhas tem (p.16 e p.18). */
                rodape={
                  <RodapeTabela
                    primeiro={fatia.primeiro}
                    ultimo={fatia.ultimo}
                    total={linhas.length}
                    pagina={fatia.pagina}
                    paginas={fatia.paginas}
                    porPagina={porPagina}
                    opcoesPorPagina={OPCOES_POR_PAGINA}
                    aoMudarPagina={setPagina}
                    aoMudarPorPagina={(novo) => {
                      setPorPagina(novo);
                      setPagina(1);
                    }}
                  />
                }
              />
              )}
            </div>

            {visao.isPending ? (
              /* A regra ainda não chegou (p.32): o título é certo, e os pesos e a
                 mediana viram linhas. */
              <section className="cartao flex flex-col gap-2 p-6">
                <h2 className="txt-secao text-tinta">A regra da fila</h2>
                <Carregando oQue="Carregando a regra da fila" className="flex flex-col gap-3 py-1">
                  {['w-3/5', 'w-1/2', 'w-2/3', 'w-2/5', 'w-1/2', 'w-4/5'].map((largura, i) => (
                    <Barra key={i} className={`h-3 ${largura}`} />
                  ))}
                </Carregando>
              </section>
            ) : null}
            {v ? (
              <section className="cartao flex flex-col gap-2 p-6">
                <h2 className="txt-secao text-tinta">A regra da fila</h2>
                <ul className="flex flex-col gap-1">
                  {ORDEM_DA_REGRA.map((s) => (
                    <li key={s} className="txt-apoio text-tinta">
                      <span className="numero">{`${formatarInteiro(v.regra.pesos[s])} ${v.regra.pesos[s] === 1 ? 'ponto' : 'pontos'}`}</span>
                      {` ${PREMISSA_DO_SINAL[s](v.regra)}`}
                    </li>
                  ))}
                </ul>
                {/* A definição do custo parado (R06, p.72), escrita como as outras premissas. */}
                {v.custoParado ? (
                  <p className="txt-apoio text-pretty text-tinta-secundaria">{v.custoParado.premissa}</p>
                ) : null}
                <p className="txt-apoio text-pretty text-tinta-secundaria">
                  {v.medianaDoCustoPorHora === null
                    ? 'Sem custo por hora no período, a parcela de custo fora da curva não se aplica.'
                    : `Mediana do custo por hora produtiva da frota no período: ${formatarReais(v.medianaDoCustoPorHora)}/h. Os pesos são provisórios.`}
                </p>
              </section>
            ) : null}

            <SecaoDoResultado />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * FERRAMENTA 3 — custo, receita e lucro, "com premissas visíveis".
 *
 * Esta seção era, até 01/10, um aviso de ausência: o resultado precisava da
 * receita, que é do módulo 14. A receita entrou e ela virou a ferramenta.
 *
 * ## O resultado é MENSAL, e o período da faixa acima não vale aqui
 *
 * A receita é de uma competência — um mês fechado, porque a medição é mensal.
 * Cruzar a receita de setembro com o custo de 15/09 a 15/10 daria um resultado
 * que não é de nenhum período, e pareceria certo porque os dois números
 * existem. Por isso esta seção tem o seu próprio seletor de mês, e a tela diz
 * qual janela de custo ele usou.
 *
 * ## As premissas não são nota de rodapé
 *
 * Elas vêm no mesmo objeto do número, e aparecem antes da tabela — não depois.
 * O risco que o v6 escreve para o módulo 11 é "chamar custo parcial de custo
 * total ou margem final", e o painel executivo é onde essa mentira custa mais:
 * o dono lê e decide. Nenhum caminho desta seção mostra o resultado sem a
 * lista do que ficou fora.
 */
/** O mês passado: a competência que quase sempre é a que se fecha. */
function competenciaPadrao(): string {
  const hoje = new Date();
  const mes = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  return `${String(mes.getFullYear())}-${String(mes.getMonth() + 1).padStart(2, '0')}-01`;
}

function SecaoDoResultado() {
  const [competencia, setCompetencia] = useState(competenciaPadrao);
  const sessao = useSessao();
  /* Sem `receita.ler` o bloco não é pedido: o servidor recusaria (403), e a
     falta de licença virava o toast "Não foi feito". Ver `pedeOResultado`. */
  const pede = pedeOResultado(sessao.data?.permissoes);
  const resultado = useQuery({
    queryKey: ['gestor', 'resultado', competencia],
    enabled: pede,
    queryFn: () => apiDoGestor.resultado(competencia),
  });
  /* 0098: quem não vê a empresa inteira recebe só a receita (`soReceita`). */
  const comCusto = resultado.data && !ehSoReceita(resultado.data) ? resultado.data : undefined;
  const soDaReceita = resultado.data && ehSoReceita(resultado.data) ? resultado.data : undefined;
  const paginaDosContratos = usePaginacao(comCusto?.contratos ?? []);
  /* 0096: "Margem" e "Resultado" só quando nada ficou fora do mês. */
  const completo = comCusto?.premissas.completo ?? false;

  const COLUNAS: readonly Coluna<ResultadoDoContrato>[] = [
    {
      chave: 'contrato',
      rotulo: 'Contrato',
      celula: (c) => (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{c.contrato}</span>
          <span className="txt-apoio text-tinta-apagada">
            {`${c.cliente} · ${formatarInteiro(c.maquinas)} ${c.maquinas === 1 ? 'máquina' : 'máquinas'}${
              (c.custoDividido ?? 0) > 0 ? ` · ${formatarReais(c.custoDividido ?? 0)} dividido` : ''
            }`}
          </span>
        </span>
      ),
    },
    {
      chave: 'receita',
      rotulo: 'Receita aprovada',
      alinhamento: 'numero',
      celula: (c) => <span className="numero whitespace-nowrap text-tinta">{formatarReais(c.receita)}</span>,
    },
    {
      chave: 'custo',
      rotulo: 'Custo direto',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (c) => <span className="numero whitespace-nowrap text-tinta">{formatarReais(c.custoDireto)}</span>,
    },
    {
      /* 0096 · o custo que o resultado usa: direto + depreciação + indireto. */
      chave: 'custoTotal',
      rotulo: 'Custo total',
      alinhamento: 'numero',
      celula: (c) => (
        <span className="flex flex-col items-end">
          <span className="numero whitespace-nowrap text-tinta">{formatarReais(c.custoTotal ?? c.custoDireto)}</span>
          <span className="txt-apoio whitespace-nowrap text-tinta-apagada">
            {`dep. ${formatarReaisCompacto(c.depreciacao ?? 0)} · ind. ${formatarReaisCompacto(c.indiretoRateado ?? 0)}`}
          </span>
        </span>
      ),
    },
    {
      chave: 'resultado',
      rotulo: rotuloDoResultado(completo),
      historia: 'NUM-PNL-08',
      alinhamento: 'numero',
      /* Colorido SÓ quando o próprio valor é o alerta (regra 3): o contrato
         que não sustenta o próprio custo direto. */
      celula: (c) =>
        c.resultadoParcial < 0 ? (
          <span className="numero whitespace-nowrap text-parada-texto">{formatarReais(c.resultadoParcial)}</span>
        ) : (
          <span className="numero whitespace-nowrap text-tinta">{formatarReais(c.resultadoParcial)}</span>
        ),
    },
    {
      chave: 'margem',
      rotulo: rotuloDaMargem(completo),
      alinhamento: 'numero',
      /* "sem medição aprovada" por extenso: 0% acusaria o contrato de margem
         ruim quando o que ele não tem é medição. */
      celula: (c) =>
        c.margemParcial === null ? (
          <span className="txt-apoio text-tinta-secundaria">{margemEscrita(null)}</span>
        ) : (
          <span className="numero whitespace-nowrap text-tinta">{margemEscrita(c.margemParcial)}</span>
        ),
    },
  ];

  return (
    <section className="cartao flex flex-col gap-4 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="txt-secao text-tinta">Custo, receita e resultado por contrato</h2>
          <p className="txt-apoio text-tinta-secundaria">
            Mensal, porque a medição é mensal — o período da faixa acima não se aplica aqui.
          </p>
        </div>
        {/* Um mês, e não um par de datas: o resultado é mensal, e oferecer
            "de" e "até" convidaria a cruzar a receita de setembro com o custo
            de meio setembro a meio outubro. */}
        {pede ? (
        <CampoDeCompetencia rotulo="Competência" valor={competencia} aoMudar={(v) => { if (v !== '') setCompetencia(v); }} />
        ) : null}
      </div>

      {sessao.data && !pede ? (
        <SemPermissao modulo="o resultado por contrato, que cruza o custo com a receita" perfil={perfilAPedir('receita.ler')} titulo="Você não vê a receita" />
      ) : resultado.isPending ? (
        /* Carregando (p.32) na forma da resposta: as premissas, os quatro
           indicadores com o rótulo certo e a tabela com o cabeçalho certo. */
        <>
          <EsqueletoTexto oQue="Carregando o resultado da competência" linhas={2} />
          <FaixaIndicadores>
            <CardIndicador carregando rotulo="Receita aprovada" icone={CircleDollarSign} tom="marca" valor="" contexto="" />
            <CardIndicador carregando rotulo="Custo total atribuído" icone={CircleDollarSign} tom="neutro" valor="" contexto="" />
            <CardIndicador carregando rotulo="Resultado" icone={CircleDollarSign} tom="operando" valor="" contexto="" />
            <CardIndicador carregando rotulo="Custo sem contrato" icone={Clock} tom="standby" valor="" contexto="" />
          </FaixaIndicadores>
          <Tabela
            rotulo="Resultado por contrato"
            colunas={COLUNAS}
            linhas={[]}
            chaveDaLinha={(c) => c.contratoId}
            alturaDupla
            carregando
            /* O esqueleto reserva o rodapé que a tabela vai ter: nada pula (p.32). */
            rodape={<RodapeTabela {...paginaDosContratos.rodape} />}
            vazio={null}
          />
        </>
      ) : resultado.isError ? (
        <p role="alert" className="txt-apoio text-parada-texto">
          {explicarFalha(resultado.error).comoResolver}
        </p>
      ) : soDaReceita ? (
        <ResultadoDoAlcance resultado={soDaReceita} />
      ) : comCusto === undefined ? null : (
        <>
          {/* AS PREMISSAS VÊM ANTES DA TABELA, e não depois: a nota de rodapé
              é a que se esquece de ler. */}
          <p className="txt-corpo text-pretty text-tinta">{porqueOResultadoEParcial(comCusto)}</p>

          {comCusto.premissas.naoInclui.length === 0 ? null : (
            <ul className="flex flex-col gap-1">
              {comCusto.premissas.naoInclui.map((o) => (
                <li key={o} className="txt-apoio text-pretty text-tinta-secundaria">
                  {`Não entrou: ${o}`}
                </li>
              ))}
            </ul>
          )}

          <FaixaIndicadores>
            <CardIndicador
              rotulo="Receita aprovada"
              icone={CircleDollarSign}
              tom="marca"
              valor={formatarReais(comCusto.totais.receita)}
              contexto="Medições aprovadas na competência"
            />
            <CardIndicador
              rotulo="Custo total atribuído"
              icone={CircleDollarSign}
              tom="neutro"
              valor={formatarReais(comCusto.totais.custoTotal ?? comCusto.totais.custoDireto)}
              contexto={`Direto ${formatarReais(comCusto.totais.custoDireto)} · depreciação ${formatarReais(comCusto.totais.depreciacao ?? 0)} · indireto ${formatarReais(comCusto.totais.indiretoRateado ?? 0)}`}
            />
            <CardIndicador
              rotulo={rotuloDoResultado(completo)}
              icone={CircleDollarSign}
              tom={comCusto.totais.resultadoParcial < 0 ? 'parada' : 'operando'}
              valor={formatarReais(comCusto.totais.resultadoParcial)}
              alerta={comCusto.totais.resultadoParcial < 0}
              contexto={`${rotuloDaMargem(completo)} ${margemEscrita(comCusto.totais.margemParcial)}`}
            />
            <CardIndicador
              historia="NUM-PNL-09"
              rotulo="Custo sem contrato"
              icone={Clock}
              tom="standby"
              valor={formatarReais(comCusto.custoNaoAtribuido.valor)}
              alerta={comCusto.custoNaoAtribuido.valor > 0}
              contexto="Fora do resultado: sem alocação, ou sem hora para dividir"
            />
          </FaixaIndicadores>

          <Tabela
            rotulo="Resultado por contrato"
            colunas={COLUNAS}
            linhas={paginaDosContratos.visiveis}
            rodape={<RodapeTabela {...paginaDosContratos.rodape} />}
            chaveDaLinha={(c) => c.contratoId}
            alturaDupla
            vazio={
              <EstadoVazio
                titulo="Nenhuma medição aprovada nesta competência"
                descricao="O resultado sai da medição aprovada. Monte e aprove as medições do mês em Custos · Medições e receita, e o cruzamento com o custo aparece aqui."
              />
            }
          />

          {/* O QUE NÃO FOI ATRIBUÍDO, com o que resolve cada caso. Esconder
              esta lista faria o custo total parar de fechar com o da frota, e
              ninguém saberia por quê. */}
          {comCusto.custoNaoAtribuido.maquinas.length === 0 ? null : (
            <div className="flex flex-col gap-2">
              <h3 className="txt-subtitulo text-tinta">Custo que não foi atribuído a contrato</h3>
              <p className="txt-apoio max-w-[80ch] text-pretty text-tinta-secundaria">
                Estas máquinas tiveram custo no mês e ele não entrou no resultado de nenhum contrato. A máquina de
                vários contratos se divide pela hora reconhecida ou apontada em cada um; sem hora nenhuma, ela não é
                dividida igual por palpite — fica aqui até a hora existir.
              </p>
              <ul className="flex flex-col gap-1">
                {comCusto.custoNaoAtribuido.maquinas.map((m) => (
                  <li key={m.equipamentoId} className="txt-apoio text-pretty text-tinta-secundaria">
                    <span className="codigo text-tinta">{m.codigo}</span>
                    {` · ${formatarReais(m.custo)} · ${motivoNaoAtribuidoEscrito(m.motivo)} · ${oQueResolveONaoAtribuido(m.motivo)}`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 0096 · as máquinas divididas entre contratos, para abrir a conta (guia 9.1). */}
          {(comCusto.custoDividido?.maquinas.length ?? 0) === 0 ? null : (
            <div className="flex flex-col gap-2">
              <h3 className="txt-subtitulo text-tinta">Máquinas divididas entre contratos</h3>
              <ul className="flex flex-col gap-1">
                {comCusto.custoDividido?.maquinas.map((m) => (
                  <li key={m.equipamentoId} className="txt-apoio text-pretty text-tinta-secundaria">
                    <span className="codigo text-tinta">{m.codigo}</span>
                    {` · ${formatarReais(m.custo)} ${baseDaDivisaoEscrita(m.base)}: ${m.partes.map((p) => `${p.contrato} ${formatarReais(p.valor)}`).join(', ')}`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="txt-apoio text-tinta-secundaria">
            {`Janela de custo: ${formatarData(new Date(`${comCusto.janela.de}T12:00:00`))} a ${formatarData(new Date(`${comCusto.janela.ate}T12:00:00`))}. `}
            {`Critério de custeio do combustível: ${rotuloDoCriterio(comCusto.criterioDeCusteio)}.`}
          </p>

          <div className="flex flex-wrap gap-2">
            <Link to="/custos" className="botao botao-terciario">
              Abrir custos
            </Link>
            <Link to="/receita" className="botao botao-terciario">
              Abrir medições e receita
            </Link>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * 0098 · O resultado de quem tem papel de filial, obra ou contrato: a receita
 * dos contratos visíveis, inteira, e o porquê de não haver custo nem margem —
 * a frase vem das premissas do servidor, e não daqui.
 */
function ResultadoDoAlcance({ resultado }: { readonly resultado: ResultadoSoDaReceita }) {
  const pagina = usePaginacao(resultado.contratos);
  const COLUNAS: readonly Coluna<ResultadoSoDaReceita['contratos'][number]>[] = [
    {
      chave: 'contrato',
      rotulo: 'Contrato',
      celula: (c) => (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{c.contrato}</span>
          <span className="txt-apoio text-tinta-apagada">{c.cliente}</span>
        </span>
      ),
    },
    {
      chave: 'receita',
      rotulo: 'Receita aprovada',
      alinhamento: 'numero',
      celula: (c) => <span className="numero whitespace-nowrap text-tinta">{formatarReais(c.receita)}</span>,
    },
    {
      chave: 'horas',
      rotulo: 'Horas reconhecidas',
      alinhamento: 'numero',
      celula: (c) => (
        <span className="numero whitespace-nowrap text-tinta">
          {formatarDecimal(c.horasReconhecidas, 1)} <span className="text-tinta-apagada">h</span>
        </span>
      ),
    },
  ];
  return (
    <>
      <p className="txt-corpo text-pretty text-tinta">Receita dos contratos do seu alcance, inteira — sem custo e sem margem.</p>
      <ul className="flex flex-col gap-1">
        {resultado.premissas.naoInclui.map((o) => (
          <li key={o} className="txt-apoio text-pretty text-tinta-secundaria">
            {`Não entrou: ${o}.`}
          </li>
        ))}
      </ul>
      <FaixaIndicadores>
        <CardIndicador
          rotulo="Receita aprovada"
          icone={CircleDollarSign}
          tom="marca"
          valor={formatarReais(resultado.totais.receita)}
          contexto="Medições aprovadas dos contratos do seu alcance"
        />
      </FaixaIndicadores>
      <Tabela
        rotulo="Receita por contrato"
        colunas={COLUNAS}
        linhas={pagina.visiveis}
        rodape={<RodapeTabela {...pagina.rodape} />}
        chaveDaLinha={(c) => c.contratoId}
        alturaDupla
        vazio={
          <EstadoVazio
            titulo="Nenhuma medição aprovada nesta competência"
            descricao="A receita sai da medição aprovada dos contratos do seu alcance. Monte e aprove as medições do mês em Custos · Medições e receita."
          />
        }
      />
      <div className="flex flex-wrap gap-2">
        <Link to="/receita" className="botao botao-terciario">
          Abrir medições e receita
        </Link>
      </div>
    </>
  );
}
